import * as React from "react";
import { Box, Button, Card, CardContent, CardHeader, Chip, Divider, Stack, Typography } from "@mui/material";
import { DownloadSimpleIcon } from "@phosphor-icons/react/dist/ssr/DownloadSimple";
import { UploadSimpleIcon } from "@phosphor-icons/react/dist/ssr/UploadSimple";
import dayjs from "dayjs";

import type {
	BriefDocumentKind,
	CampaignBrief,
	CampaignBriefDetail,
	CampaignBriefDocument,
	CampaignBriefStatus,
} from "@/hooks/api/use-campaign-briefs";

import { formatPrice } from "../rate-card/format";
import {
	describeSchedule,
	DOCUMENTS,
	formatBytes,
	languageLabel,
	regionLabel,
	REGIONS,
	STATUS_COLORS,
	STATUS_LABELS,
} from "./constants";

export function StatusChip({ status }: { status: CampaignBriefStatus }): React.JSX.Element {
	return (
		<Chip
			label={STATUS_LABELS[status] ?? status}
			size="small"
			color={STATUS_COLORS[status] ?? "default"}
			variant={status === "draft" ? "outlined" : "filled"}
		/>
	);
}

export const formatDate = (date: string) => dayjs(date).format("ddd D MMM YYYY");

/** The regions' own clocks, so "09:00" reads as 09:00 SGT and 09:00 WIB rather than one moment. */
export const clocksFor = (regions: string[]) =>
	regions.map((code) => REGIONS.find(({ value }) => value === code)?.clock ?? code).join(" / ");

const yesNo = (required: boolean, attached: boolean) =>
	required
		? attached
			? "Required, attached"
			: "Required, not attached yet"
		: attached
			? "Not required, attached"
			: "Not required";

function Row({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
	return (
		<Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0.5, sm: 2 }} sx={{ py: 1 }}>
			<Typography variant="body2" color="text.secondary" sx={{ minWidth: 180 }}>
				{label}
			</Typography>
			<Box sx={{ flex: "1 1 auto" }}>{children}</Box>
		</Stack>
	);
}

/** Everything the client put on the form. */
export function BriefSummary({ detail }: { detail: CampaignBriefDetail }): React.JSX.Element {
	const { brief, documents } = detail;
	const has = (kind: BriefDocumentKind) => documents.some((document) => document.kind === kind);

	return (
		<Card>
			<CardHeader title="Request" />
			<Divider />
			<CardContent>
				<Row label="Campaign name">
					<Typography variant="body2">{brief.title}</Typography>
				</Row>
				<Row label="First dial">
					<Typography variant="body2">{formatDate(brief.dialDate)}</Typography>
				</Row>
				<Row label="Redials">
					{brief.redialDates.length > 0 ? (
						<Stack spacing={0.5}>
							{brief.redialDates.map((date) => (
								<Typography key={date} variant="body2">
									{formatDate(date)}
								</Typography>
							))}
						</Stack>
					) : (
						<Typography variant="body2" color="text.secondary">
							None
						</Typography>
					)}
				</Row>
				<Row label="Dialling hours">
					<Typography variant="body2">
						{describeSchedule(brief.schedule)}{" "}
						<Typography component="span" variant="body2" color="text.secondary">
							local time ({clocksFor(brief.regions)})
						</Typography>
					</Typography>
				</Row>
				<Row label="Agent">
					<Typography variant="body2">
						{brief.agentChoice === "existing" ? (brief.agentName ?? "Existing agent") : "New agent, from the call flow"}
					</Typography>
				</Row>
				<Row label="Extra agents">
					{brief.extraAgents.length > 0 ? (
						<Stack spacing={0.5}>
							{brief.extraAgents.map((agent, index) => (
								<Typography key={index} variant="body2">
									{agent}
								</Typography>
							))}
						</Stack>
					) : (
						<Typography variant="body2" color="text.secondary">
							None
						</Typography>
					)}
				</Row>
				<Row label="Markets">
					<Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
						{brief.markets.map(({ region, language }) => (
							<Chip
								key={region}
								label={`${regionLabel(region)} · ${languageLabel(language)}`}
								size="small"
								variant="outlined"
							/>
						))}
					</Stack>
				</Row>
				<Row label="PO">
					<Typography variant="body2">{yesNo(brief.poRequired, has("po"))}</Typography>
				</Row>
				<Row label="SOW">
					<Typography variant="body2">{yesNo(brief.sowRequired, has("sow"))}</Typography>
				</Row>
				<Row label="Optional services">
					{brief.addOns.length > 0 ? (
						<Stack spacing={0.5}>
							{brief.addOns.map((addOn) => (
								<Typography key={addOn.code} variant="body2">
									{addOn.name}{" "}
									<Typography component="span" variant="body2" color="text.secondary">
										· {formatPrice(addOn)}
									</Typography>
								</Typography>
							))}
						</Stack>
					) : (
						<Typography variant="body2" color="text.secondary">
							None
						</Typography>
					)}
				</Row>
				<Row label="Notes">
					<Typography
						variant="body2"
						sx={{ whiteSpace: "pre-wrap" }}
						color={brief.notes ? "text.primary" : "text.secondary"}
					>
						{brief.notes || "None"}
					</Typography>
				</Row>
				<Row label="Submitted">
					<Typography variant="body2">
						{brief.submittedAt
							? `${dayjs(brief.submittedAt).format("D MMM YYYY, HH:mm")} by ${brief.submittedByName ?? "unknown"}${
									detail.submittedByEmail ? ` (${detail.submittedByEmail})` : ""
								}`
							: "Not yet"}
					</Typography>
				</Row>
			</CardContent>
		</Card>
	);
}

/** What the request came to on the rate card when it was saved. */
export function BriefEstimateCard({ brief }: { brief: CampaignBrief }): React.JSX.Element | null {
	const { estimate } = brief;
	if (!estimate) return null;

	return (
		<Card>
			<CardHeader title="Estimate" subheader="From the rate card when the request was saved" />
			<Divider />
			<CardContent>
				<Stack spacing={1}>
					{estimate.lines.map((line, index) => (
						<Stack key={index} direction="row" spacing={2} sx={{ justifyContent: "space-between" }}>
							<Box sx={{ minWidth: 0 }}>
								<Typography variant="body2">{line.label}</Typography>
								{line.label === line.name ? null : (
									<Typography variant="caption" color="text.secondary">
										{line.name}
									</Typography>
								)}
							</Box>
							<Typography variant="body2" sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
								{formatPrice({ ...line, currency: estimate.currency, unit: null })}
							</Typography>
						</Stack>
					))}
				</Stack>
				<Divider sx={{ my: 1.5 }} />
				<Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "baseline" }}>
					<Typography variant="subtitle2">One-off total</Typography>
					<Typography variant="h6" sx={{ fontVariantNumeric: "tabular-nums" }}>
						{formatPrice({ amount: estimate.total, currency: estimate.currency, unit: null, priceNote: null })}
					</Typography>
				</Stack>
				<Typography variant="caption" color="text.secondary">
					Excludes GST and talk time. Not a quote until we confirm it.
				</Typography>
			</CardContent>
		</Card>
	);
}

interface BriefFilesProps {
	brief: CampaignBrief;
	documents: CampaignBriefDocument[];
	onDownload: (document: CampaignBriefDocument) => void;
	/** Shown for a PO or SOW that was said to be required and has not arrived. */
	onUpload?: (kind: BriefDocumentKind, file: File) => void;
	uploading?: boolean;
}

export function BriefFiles({ brief, documents, onDownload, onUpload, uploading }: BriefFilesProps): React.JSX.Element {
	const missing = (["po", "sow"] as const).filter(
		(kind) =>
			(kind === "po" ? brief.poRequired : brief.sowRequired) && !documents.some((document) => document.kind === kind)
	);
	const open = !["completed", "cancelled"].includes(brief.status);

	return (
		<Card>
			<CardHeader title="Files" />
			<Divider />
			<CardContent>
				<Stack divider={<Divider />}>
					{documents.map((document) => (
						<Stack key={document.id} direction="row" spacing={2} sx={{ alignItems: "center", py: 1 }}>
							<Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
								<Typography variant="subtitle2">{DOCUMENTS[document.kind]?.label ?? document.kind}</Typography>
								<Typography variant="body2" color="text.secondary" noWrap title={document.originalFilename}>
									{document.originalFilename} · {formatBytes(document.sizeBytes)}
								</Typography>
							</Box>
							<Button size="small" startIcon={<DownloadSimpleIcon />} onClick={() => onDownload(document)}>
								Download
							</Button>
						</Stack>
					))}
					{onUpload && open
						? missing.map((kind) => (
								<MissingPaperwork key={kind} kind={kind} onUpload={onUpload} uploading={uploading} />
							))
						: null}
				</Stack>
				{documents.length === 0 && missing.length === 0 ? (
					<Typography variant="body2" color="text.secondary">
						No files.
					</Typography>
				) : null}
			</CardContent>
		</Card>
	);
}

function MissingPaperwork({
	kind,
	onUpload,
	uploading,
}: {
	kind: BriefDocumentKind;
	onUpload: (kind: BriefDocumentKind, file: File) => void;
	uploading?: boolean;
}): React.JSX.Element {
	const input = React.useRef<HTMLInputElement>(null);
	const { label, accept } = DOCUMENTS[kind];

	return (
		<Stack direction="row" spacing={2} sx={{ alignItems: "center", py: 1 }}>
			<Box sx={{ flex: "1 1 auto" }}>
				<Typography variant="subtitle2">{label}</Typography>
				<Typography variant="body2" color="warning.main">
					Required, not attached yet
				</Typography>
			</Box>
			<Button
				size="small"
				variant="outlined"
				startIcon={<UploadSimpleIcon />}
				disabled={uploading}
				onClick={() => input.current?.click()}
			>
				Upload {label}
			</Button>
			<input
				ref={input}
				type="file"
				hidden
				accept={accept}
				onChange={(event) => {
					const file = event.target.files?.[0];
					if (file) onUpload(kind, file);
					event.target.value = "";
				}}
			/>
		</Stack>
	);
}

/** Who moved the request and when, oldest first. */
export function BriefTimeline({ detail }: { detail: CampaignBriefDetail }): React.JSX.Element {
	const fileName = (id: string | null) => detail.documents.find((document) => document.id === id)?.originalFilename;

	return (
		<Card>
			<CardHeader title="Timeline" />
			<Divider />
			<CardContent>
				{detail.events.length === 0 ? (
					<Typography variant="body2" color="text.secondary">
						Nothing yet.
					</Typography>
				) : (
					<Stack spacing={2}>
						{detail.events.map((event) => (
							<Box key={event.id}>
								<Typography variant="body2">
									{event.action === "document_downloaded"
										? `${event.actorName ?? "Someone"} downloaded ${fileName(event.documentId) ?? "a file"}`
										: event.fromStatus === "draft"
											? `${event.actorName ?? "Someone"} submitted the request`
											: `${event.actorName ?? "Someone"} moved it to ${STATUS_LABELS[event.toStatus!] ?? event.toStatus}`}
								</Typography>
								<Typography variant="caption" color="text.secondary">
									{dayjs(event.at).format("D MMM YYYY, HH:mm")}
								</Typography>
								{event.note ? (
									<Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap", mt: 0.5 }}>
										{event.note}
									</Typography>
								) : null}
							</Box>
						))}
					</Stack>
				)}
			</CardContent>
		</Card>
	);
}
